import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Joyride, EVENTS, ACTIONS, type EventData } from 'react-joyride';
import { useTheme } from '../context/ThemeContext';
import Modal from '../components/Modal';
import { TUTORIAL_FLAG, buildSteps } from './steps';

export function hasSeenTutorial(): boolean {
  try {
    return localStorage.getItem(TUTORIAL_FLAG) === '1';
  } catch {
    return true;
  }
}

export function markTutorialSeen() {
  try { localStorage.setItem(TUTORIAL_FLAG, '1'); } catch { /* ignore */ }
}

export function resetTutorial() {
  try { localStorage.removeItem(TUTORIAL_FLAG); } catch { /* ignore */ }
}

export default function TutorialProvider() {
  const location = useLocation();
  const navigate = useNavigate();
  const { colors } = useTheme();

  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [showSkipConfirm, setShowSkipConfirm] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const skipReturnIndexRef = useRef(0);

  // Keep the latest pathname accessible to the `before` hook without retriggering.
  const pathRef = useRef(location.pathname);
  useEffect(() => { pathRef.current = location.pathname; }, [location.pathname]);

  // Steps are built once with stable refs; navigate / pathRef captured.
  const steps = useMemo(() => buildSteps(navigate, () => pathRef.current), [navigate]);

  // Show welcome modal the first time the user lands on /home post-login.
  useEffect(() => {
    if (location.pathname !== '/home') return;
    if (hasSeenTutorial()) return;
    if (run || showWelcome) return;
    const t = setTimeout(() => setShowWelcome(true), 600);
    return () => clearTimeout(t);
  }, [location.pathname, run, showWelcome]);

  const acceptWelcome = () => {
    setShowWelcome(false);
    setStepIndex(0);
    setRun(true);
  };

  const declineWelcome = () => {
    setShowWelcome(false);
    markTutorialSeen();
  };

  const finishAndMark = () => {
    setRun(false);
    setStepIndex(0);
    markTutorialSeen();
  };

  const handleEvent = (data: EventData) => {
    // Track current step index for controlled mode + back/forward stability.
    if (data.type === EVENTS.STEP_AFTER) {
      const next = data.action === ACTIONS.PREV ? data.index - 1 : data.index + 1;
      if (next < 0 || next >= steps.length) {
        // We let TOUR_END handle finalization, but advance to be safe.
        setStepIndex(Math.max(0, Math.min(steps.length - 1, next)));
      } else {
        setStepIndex(next);
      }
    }

    // The user pressed Skip (or closed in skip mode). Intercept with a confirm.
    // NB: the Actions union in react-joyride v3 types omits 'skip' even though
    // ACTIONS.SKIP fires at runtime; compare via string.
    const action = data.action as unknown as string;
    if (action === 'skip') {
      skipReturnIndexRef.current = data.index;
      setRun(false);
      setShowSkipConfirm(true);
      return;
    }

    if (data.type === EVENTS.TOUR_END && action !== 'skip') {
      finishAndMark();
    }
  };

  const confirmSkip = () => {
    setShowSkipConfirm(false);
    finishAndMark();
  };

  const cancelSkip = () => {
    setShowSkipConfirm(false);
    setStepIndex(skipReturnIndexRef.current);
    setRun(true);
  };

  return (
    <>
      {run && (
        <Joyride
          steps={steps}
          stepIndex={stepIndex}
          run={run}
          continuous
          scrollToFirstStep
          onEvent={handleEvent}
          locale={{ back: 'Back', close: 'Close', last: 'Got it', next: 'Next', skip: 'Skip' }}
          options={{
            primaryColor: colors.accent,
            textColor: colors.text,
            backgroundColor: colors.card,
            arrowColor: colors.card,
            overlayColor: 'rgba(2, 6, 23, 0.65)',
            zIndex: 10000,
            targetWaitTimeout: 4000,
          }}
          styles={{
            tooltip: { borderRadius: 14, padding: 18 },
            tooltipTitle: { fontSize: 16, fontWeight: 800 },
            tooltipContent: { fontSize: 13, lineHeight: 1.5 },
            buttonPrimary: { borderRadius: 999, padding: '8px 16px', fontWeight: 800 },
            buttonBack: { color: colors.textSub, fontWeight: 700 },
            buttonSkip: { color: colors.textSub, fontWeight: 700 },
          }}
        />
      )}

      <Modal open={showSkipConfirm} onClose={cancelSkip}>
        <div style={{ fontSize: 18, fontWeight: 800, color: colors.text, marginBottom: 10 }}>
          Skip the tutorial?
        </div>
        <div style={{ fontSize: 14, color: colors.textSub, lineHeight: 1.55, marginBottom: 18 }}>
          You can replay it anytime from <strong style={{ color: colors.text }}>Profile &rarr; Replay Tutorial</strong>, or
          read a written guide under <strong style={{ color: colors.text }}>How it works</strong>.
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={cancelSkip}
            style={{
              flex: 1, minHeight: 48, borderRadius: 12,
              backgroundColor: colors.card, border: `1px solid ${colors.cardBorder}`,
              cursor: 'pointer', fontSize: 14, fontWeight: 800, color: colors.text,
            }}
          >
            Keep going
          </button>
          <button
            onClick={confirmSkip}
            style={{
              flex: 1, minHeight: 48, borderRadius: 12,
              backgroundColor: colors.accent, border: 'none',
              cursor: 'pointer', fontSize: 14, fontWeight: 800, color: '#020617',
            }}
          >
            Skip tour
          </button>
        </div>
      </Modal>

      <Modal open={showWelcome} onClose={declineWelcome}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
          <div style={{
            width: 84, height: 84, borderRadius: 18, backgroundColor: '#000',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            overflow: 'hidden', marginBottom: 16,
          }}>
            <img src="/icons/icon-512.png" alt="" style={{ width: 70, height: 70, objectFit: 'contain' }} />
          </div>
          <div style={{ fontSize: 24, fontWeight: 900, letterSpacing: 0.5, color: colors.text, marginBottom: 4 }}>
            Welcome to AGA
          </div>
          <div style={{
            fontSize: 13, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase',
            color: colors.accentText, marginBottom: 16,
          }}>
            All Game Arena
          </div>
          <div style={{ fontSize: 14, lineHeight: 1.55, color: colors.textSub, marginBottom: 24 }}>
            One wallet for all your favorite games. Take a quick tour (about a minute) so you know where everything lives.
          </div>
          <button
            onClick={acceptWelcome}
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              minWidth: 180, minHeight: 50, padding: '14px 24px',
              borderRadius: 999, backgroundColor: colors.accent, color: '#020617',
              border: 'none', cursor: 'pointer',
              fontSize: 15, fontWeight: 800,
            }}
          >
            Let's Go
            <span style={{ fontSize: 13 }}>→</span>
          </button>
          <button
            onClick={declineWelcome}
            style={{
              marginTop: 12, padding: '10px 20px', minHeight: 40,
              background: 'transparent', border: 'none', cursor: 'pointer',
              fontSize: 13, fontWeight: 700, color: colors.textSub,
            }}
          >
            Skip for now
          </button>
        </div>
      </Modal>
    </>
  );
}
